import {
    callMediaOf,
    initialCallMedia,
    reduceMatrixCallSession,
    type MatrixCallFailure,
    type MatrixCallIdentity,
    type MatrixCallMediaIntent,
    type MatrixCallMediaState,
    type MatrixCallSessionEvent,
    type MatrixCallSessionState,
} from "./CallSessionState";

export interface MatrixVoiceCallRequest {
    readonly roomId: string;
    readonly remoteUserId?: string;
    readonly backendCallId?: string;
    readonly video?: boolean;
    readonly beforeSignalling?: (call: MatrixCallIdentity) => Promise<void>;
}

export interface MatrixCallStartOptions {
    readonly video: boolean;
}

export interface MatrixCallAnswerOptions {
    readonly video?: boolean;
}

export interface MatrixCallEnginePort {
    createVoiceCall(request: MatrixVoiceCallRequest): MatrixCallIdentity;
    startVoiceCall(call: MatrixCallIdentity, options: MatrixCallStartOptions): Promise<void>;
    accept(call: MatrixCallIdentity, options: MatrixCallAnswerOptions): Promise<void>;
    reject(call: MatrixCallIdentity): Promise<void>;
    hangUp(call: MatrixCallIdentity): Promise<void>;
    setMuted(muted: boolean): Promise<void>;
    setOutputVolume(volume: number): void;
    setLocalVideo(enabled: boolean): Promise<boolean>;
    setScreenSharing(enabled: boolean): Promise<boolean>;
}

export interface MatrixCallLogPort {
    transition(
        previous: MatrixCallSessionState,
        next: MatrixCallSessionState,
        event: MatrixCallSessionEvent,
    ): void;
}

type StateListener = (state: MatrixCallSessionState) => void;

export class MatrixCallCoordinator {
    private state: MatrixCallSessionState = { kind: "idle" };
    private readonly listeners = new Set<StateListener>();
    private commandInFlight: "accept" | "reject" | "hang_up" | null = null;

    constructor(
        private readonly engine: MatrixCallEnginePort,
        private readonly log: MatrixCallLogPort,
    ) {}

    getState(): MatrixCallSessionState {
        return this.state;
    }

    subscribe(listener: StateListener): () => void {
        this.listeners.add(listener);
        listener(this.state);
        return () => this.listeners.delete(listener);
    }

    async startVoiceCall(request: MatrixVoiceCallRequest): Promise<MatrixCallIdentity> {
        if (
            this.state.kind !== "idle" &&
            this.state.kind !== "ended" &&
            this.state.kind !== "failed"
        ) {
            throw new Error("A Matrix call is already active");
        }

        let call: MatrixCallIdentity | undefined;
        const video = request.video === true;
        try {
            call = this.engine.createVoiceCall(request);
            this.apply({
                type: "outgoing_started",
                call,
                direction: "outgoing",
                startedAtMs: Date.now(),
                media: initialCallMedia(video ? "video" : "audio"),
            });
            if (request.beforeSignalling) await request.beforeSignalling(call);
            if (this.getState().kind !== "connecting") throw new Error("Prepared call is no longer active");
            await this.engine.startVoiceCall(call, {video});
            return call;
        } catch (error) {
            this.fail(call, "signalling_failed", error);
            throw error;
        }
    }

    registerIncoming(call: MatrixCallIdentity, startedAtMs = Date.now(), intent: MatrixCallMediaIntent = "audio"): void {
        this.apply({
            type: "incoming_received",
            call,
            direction: "incoming",
            startedAtMs,
            media: {...initialCallMedia(intent), localVideo: false},
        });
    }

    getMedia(): MatrixCallMediaState | undefined {
        return callMediaOf(this.state);
    }

    async accept(options: MatrixCallAnswerOptions = {}): Promise<void> {
        const state = this.state;
        if (state.kind !== "ringing" || this.commandInFlight !== null) return;

        this.commandInFlight = "accept";
        try {
            await this.engine.accept(state.call, options);
            this.apply({
                type: "local_accepted",
                call: state.call,
                acceptedAtMs: Date.now(),
            });
        } catch (error) {
            this.fail(state.call, "signalling_failed", error);
            throw error;
        } finally {
            this.commandInFlight = null;
        }
    }

    async reject(): Promise<void> {
        const state = this.state;
        if (state.kind !== "ringing" || this.commandInFlight !== null) return;

        this.commandInFlight = "reject";
        try {
            await this.engine.reject(state.call);
            this.end(state.call, "rejected");
        } finally {
            this.commandInFlight = null;
        }
    }

    async hangUp(): Promise<void> {
        const state = this.state;
        if (
            (state.kind !== "ringing" &&
                state.kind !== "connecting" &&
                state.kind !== "connected" &&
                state.kind !== "reconnecting") ||
            this.commandInFlight !== null
        ) {
            return;
        }

        this.commandInFlight = "hang_up";
        try {
            await this.engine.hangUp(state.call);
            this.end(state.call, "local_hangup");
        } finally {
            this.commandInFlight = null;
        }
    }

    async setMuted(muted: boolean): Promise<void> {
        await this.engine.setMuted(muted);
    }

    setOutputVolume(volume: number): void {
        this.engine.setOutputVolume(volume);
    }

    async setLocalVideo(enabled: boolean): Promise<boolean> {
        const state = this.state;
        if (state.kind !== "connecting" && state.kind !== "connected" && state.kind !== "reconnecting") return false;
        const active = await this.engine.setLocalVideo(enabled);
        this.updateMedia(state.call, {localVideo: active});
        return active;
    }

    async setScreenSharing(enabled: boolean): Promise<boolean> {
        const state = this.state;
        if (state.kind !== "connected" && state.kind !== "reconnecting") return false;
        const active = await this.engine.setScreenSharing(enabled);
        this.updateMedia(state.call, {localScreenShare: active});
        return active;
    }

    updateMedia(call: MatrixCallIdentity, media: Partial<MatrixCallMediaState>): void {
        this.apply({type: "media_changed", call, media});
    }

    engineConnecting(call: MatrixCallIdentity): void {
        this.apply({ type: "transport_connecting", call });
    }

    engineConnected(call: MatrixCallIdentity, connectedAtMs = Date.now()): void {
        this.apply({ type: "media_connected", call, connectedAtMs });
    }

    engineInterrupted(call: MatrixCallIdentity, interruptedAtMs = Date.now()): void {
        this.apply({ type: "transport_interrupted", call, interruptedAtMs });
    }

    engineReplaced(
        call: MatrixCallIdentity,
        replacement: MatrixCallIdentity,
    ): void {
        this.apply({type: "call_replaced", call, replacement});
    }

    engineEnded(call: MatrixCallIdentity, reason: string): void {
        this.end(call, reason);
    }

    engineFailed(call: MatrixCallIdentity | undefined, error: unknown): void {
        this.fail(call, "transport_failed", error);
    }

    reset(): void {
        this.apply({ type: "reset" });
    }

    private end(call: MatrixCallIdentity, reason: string): void {
        this.apply({ type: "ended", call, endedAtMs: Date.now(), reason });
    }

    private fail(
        call: MatrixCallIdentity | undefined,
        failure: MatrixCallFailure,
        error: unknown,
    ): void {
        this.apply({
            type: "failed",
            call,
            failedAtMs: Date.now(),
            failure,
            detail: error instanceof Error ? error.message : String(error),
        });
    }

    private apply(event: MatrixCallSessionEvent): void {
        const previous = this.state;
        const next = reduceMatrixCallSession(previous, event);
        if (next === previous) return;

        this.state = next;
        this.log.transition(previous, next, event);
        this.listeners.forEach(listener => listener(next));
    }
}
